import { Component } from '@theme/component';

/**
 * Botic Rose -- inline header search.
 *
 * Wraps the theme's native `<predictive-search-component>` in an always-visible
 * field on desktop and an icon that expands a compact bar below the header on
 * mobile. All search behaviour (predictive fetch, keyboard navigation, submit,
 * reset) stays with `predictive-search.js`. On top of that this element merges a
 * deterministic set of title-prefix matches into the results, so every product
 * whose title STARTS WITH the query is guaranteed to appear -- Shopify's
 * predictive search occasionally omits valid prefixes ("velou", "20" can come
 * back empty). See `#mergePrefixMatches`.
 *
 * @extends {Component}
 */

/**
 * Shared, lazily-built product prefix index. The catalogue is identical for the
 * desktop and mobile header-search instances, so it is parsed at most once.
 * @type {{ order: number, handle: string, url: string, norm: string, node: HTMLElement }[] | null}
 */
let sharedIndex = null;

/** Localised "Products" heading, read off the index <template>. */
let sharedProductsTitle = '';

/** Product rows shown in the compact dropdown (matches Horizon's native limit). */
const DISPLAY_LIMIT = 8;

/**
 * Case-, diacritics- and whitespace-insensitive normalisation. Shared by the
 * index and the live query so `"Velours"` and `"velou"` compare cleanly, and
 * numeric titles fall out of the same string logic (`"20".startsWith("2")`).
 * @param {unknown} value
 * @returns {string}
 */
function normalize(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The product handle for a rendered card, used to de-duplicate local matches
 * against Shopify's predictive results.
 * @param {Element | null | undefined} card
 * @returns {string}
 */
function handleFromCard(card) {
  const explicit = card?.getAttribute?.('data-handle');
  if (explicit) return explicit.toLowerCase();

  const href = card?.querySelector?.('a[href*="/products/"]')?.getAttribute('href') || '';
  const match = href.match(/\/products\/([^/?#]+)/);
  if (match) return decodeURIComponent(match[1]).toLowerCase();

  // Last resort: fold the visible title so identical titles still collapse.
  return normalize(card?.querySelector?.('.resource-card__title, .resource-card__link .visually-hidden')?.textContent);
}

/**
 * Parse the Liquid-rendered `<template id="HeaderSearchPrefixIndex">` once.
 * A template's content is inert (no layout, no image fetches) until cloned.
 * @returns {{ order: number, handle: string, url: string, norm: string, node: HTMLElement }[]}
 */
function buildIndex() {
  if (sharedIndex) return sharedIndex;

  const template = document.getElementById('HeaderSearchPrefixIndex');
  if (!(template instanceof HTMLTemplateElement)) {
    sharedIndex = [];
    return sharedIndex;
  }

  sharedProductsTitle = template.dataset.productsTitle || '';
  sharedIndex = Array.from(template.content.querySelectorAll('li[data-prefix-card]')).map((node, order) => ({
    order,
    handle: (node.getAttribute('data-handle') || '').toLowerCase(),
    url: node.querySelector('a[href*="/products/"]')?.getAttribute('href') || '',
    norm: normalize(node.getAttribute('data-title')),
    node: /** @type {HTMLElement} */ (node),
  }));
  return sharedIndex;
}

/**
 * @param {string[]} a
 * @param {string[]} b
 * @returns {boolean}
 */
function sameOrder(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

class HeaderSearchComponent extends Component {
  /** @type {MutationObserver | null} */
  #resultsObserver = null;

  /** Guards `#mergePrefixMatches` against reacting to its own DOM writes. */
  #isApplying = false;

  /** Collapse the mobile bar when a pointer press lands outside this element. */
  #handleDocumentPointerDown = (event) => {
    if (!this.hasAttribute('data-expanded')) return;
    if (event.composedPath().includes(this)) return;
    this.collapse();
  };

  /** Escape collapses the mobile bar (predictive-search also clears its own results). */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('data-expanded')) {
      this.collapse();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    this.addEventListener('keydown', this.#handleKeydown);

    const results = this.#results;
    if (results) {
      this.#resultsObserver = new MutationObserver(this.#handleResultsMutation);
      this.#resultsObserver.observe(results, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#resultsObserver?.disconnect();
    this.#resultsObserver = null;
  }

  get #toggleButton() {
    return this.querySelector('.header-search__toggle');
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="q"]'));
  }

  /** The container `predictive-search.js` morphs its results into. */
  get #results() {
    return this.querySelector('.header-search__results');
  }

  /** Bound to the mobile icon via `on:click="/toggle"`. */
  toggle() {
    this.hasAttribute('data-expanded') ? this.collapse() : this.expand();
  }

  expand() {
    this.setAttribute('data-expanded', '');
    this.#toggleButton?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      const input = this.#input;
      if (input instanceof HTMLInputElement) input.focus();
    });
  }

  collapse() {
    this.removeAttribute('data-expanded');
    this.#toggleButton?.setAttribute('aria-expanded', 'false');
  }

  /* --- deterministic client-side prefix matching -----------------------------
     Shopify's predictive search stays the source of truth for semantic matches
     (tags, vendor, body) and for how many products exist. After each of its
     morphs we merge in a guaranteed set of title-prefix matches drawn from a
     tiny in-memory index of the catalogue (title + prerendered card, built once
     from a <template>):

       A. local title-prefix matches -> exact title first, then alphabetical
       B. then any predictive-search products not already shown
       C. no local prefix match  -> predictive search's own results, untouched
       D. nothing either way     -> predictive search's empty / "no results" state

     "rose" is not a prefix of "Velours de Rose ..." so it yields no local match
     and case C applies. No network per keystroke and no polling: the index is a
     clone-on-demand array and the merge runs off predictive-search's existing
     morph via one guarded MutationObserver (it writes only when the rendered
     order actually differs, so it converges without looping). */

  /**
   * @param {string} query - normalised, non-empty
   * @returns {{ order: number, handle: string, url: string, norm: string, node: HTMLElement }[]}
   */
  #localMatches(query) {
    const index = buildIndex();
    if (!query || index.length === 0) return [];

    return index
      .filter((entry) => entry.norm.startsWith(query))
      .sort((a, b) => {
        const aExact = a.norm === query ? 0 : 1;
        const bExact = b.norm === query ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        if (a.norm !== b.norm) return a.norm < b.norm ? -1 : 1;
        return a.order - b.order;
      });
  }

  #handleResultsMutation = () => {
    if (this.#isApplying) return;
    this.#isApplying = true;
    try {
      this.#mergePrefixMatches();
    } finally {
      // Drop the mutation records our own writes just queued.
      this.#resultsObserver?.takeRecords();
      this.#isApplying = false;
    }
  };

  #mergePrefixMatches() {
    const results = this.#results;
    if (!results) return;

    // Present only after a real predictive response (including a zero-result one).
    const inner = results.querySelector('.predictive-search-results__inner[data-search-results]');
    if (!inner) return;

    const query = normalize(this.#input?.value);
    if (!query) return;

    const matches = this.#localMatches(query);
    if (matches.length === 0) {
      // Case C: no local prefix match -> leave predictive search's own order
      // alone, but still dress whatever it returned in the editorial chrome.
      const nativeSection = inner.querySelector('#predictive-search-products');
      if (nativeSection) this.#applyChrome(inner, nativeSection);
      return;
    }

    let section = inner.querySelector('#predictive-search-products');
    const nativeCards = section
      ? Array.from(section.querySelectorAll('li.predictive-search-results__card--product'))
      : [];

    // A: local prefix matches, then B: predictive products not already shown.
    const seen = new Set(matches.map((entry) => entry.handle));
    /** @type {{ handle: string, node: Element, local: boolean }[]} */
    const desired = matches.map((entry) => ({ handle: entry.handle, node: entry.node, local: true }));
    for (const card of nativeCards) {
      if (desired.length >= DISPLAY_LIMIT) break;
      const handle = handleFromCard(card);
      if (seen.has(handle)) continue;
      seen.add(handle);
      desired.push({ handle, node: card, local: false });
    }
    desired.length = Math.min(desired.length, DISPLAY_LIMIT);

    const desiredHandles = desired.map((entry) => entry.handle);
    const currentHandles = section
      ? Array.from(section.querySelectorAll('li.predictive-search-results__card--product')).map(handleFromCard)
      : [];

    if (!(section && sameOrder(currentHandles, desiredHandles))) {
      if (!section) {
        section = document.createElement('div');
        section.id = 'predictive-search-products';
        section.className = 'predictive-search-results__products';

        const heading = document.createElement('h4');
        heading.className = 'predictive-search-results__title';
        heading.textContent = sharedProductsTitle;

        const list = document.createElement('ul');
        list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';

        section.append(heading, list);

        const anchor =
          inner.querySelector('.predictive-search-results__wrapper-queries') ||
          inner.querySelector('[role="status"]');
        if (anchor && anchor.parentElement === inner) anchor.after(section);
        else inner.prepend(section);
      }

      const list = section.querySelector('.predictive-search-results__wrapper-products');
      if (list) {
        list.replaceChildren(...desired.map((entry) => (entry.local ? entry.node.cloneNode(true) : entry.node)));
      }
    }

    // We now show products: drop predictive search's "no results" note.
    inner.querySelector('.predictive-search-results__no-results')?.remove();

    // Keep Enter honest: one visible result -> jump to it; more than one -> follow
    // the keyboard selection instead of any stale single-result shortcut.
    const shortcut = inner.querySelector('[data-single-result-url]');
    if (desired.length > 1) {
      shortcut?.remove();
    } else if (!shortcut && matches[0]?.url) {
      const marker = document.createElement('div');
      marker.setAttribute('data-single-result-url', matches[0].url);
      inner.appendChild(marker);
    }

    // Don't let a screen reader announce "no results" while results are shown.
    const status = inner.querySelector('[role="status"]');
    if (status) status.textContent = '';

    this.#applyChrome(inner, section);
  }

  /**
   * Editorial chrome for the dropdown -- display only, no effect on which
   * products show, their order, or keyboard navigation:
   *   - rewrites the section label to "Rezultate (N)" (N = rows actually shown);
   *   - adds/updates a discreet "Vezi toate rezultatele pentru …" link.
   * Idempotent: it only touches the DOM when a value actually changes.
   * @param {Element} inner
   * @param {Element} section
   */
  #applyChrome(inner, section) {
    const count = section.querySelectorAll('li.predictive-search-results__card--product').length;

    const heading = section.querySelector('.predictive-search-results__title');
    if (heading) {
      const label = `${sharedProductsTitle || 'Rezultate'} (${count})`;
      if (heading.textContent.trim() !== label) heading.textContent = label;
    }

    const term = (this.#input?.value || '').trim();
    let seeAll = inner.querySelector('.header-search__see-all');

    if (!term || count === 0) {
      seeAll?.remove();
      return;
    }

    if (!seeAll) {
      seeAll = document.createElement('a');
      seeAll.className = 'header-search__see-all';
      inner.appendChild(seeAll);
    } else if (seeAll !== inner.lastElementChild) {
      inner.appendChild(seeAll);
    }

    const base = this.querySelector('form')?.getAttribute('action') || '/search';
    const href = `${base}?q=${encodeURIComponent(term)}`;
    if (seeAll.getAttribute('href') !== href) seeAll.setAttribute('href', href);

    const text = `Vezi toate rezultatele pentru „${term}” →`;
    if (seeAll.textContent !== text) seeAll.textContent = text;
  }
}

if (!customElements.get('header-search-component')) {
  customElements.define('header-search-component', HeaderSearchComponent);
}
